import type { ServerTheme } from '@sirocco/shared';

/**
 * Jour ou nuit.
 *
 * Le mode nuit ne repeint que les jetons `--s-*` : la structure, les
 * tailles et les positions ne bougent pas d'un pixel. Ce n'est pas un
 * second jeu de maquettes, c'est la meme application sous une autre
 * lumiere.
 *
 * La palette nocturne se **derive** de celle du spot plutot que d'etre
 * ecrite en dur : le jour ou une deuxieme ville ouvre, elle heritera de
 * sa nuit sans qu'on touche a quoi que ce soit. L'accent garde sa teinte
 * — le terracotta de Marrakech reste orange — mais s'eclaircit pour
 * tenir sur un fond sombre, ou une couleur foncee ne se voit plus.
 */
export type Ambiance = 'day' | 'night';

/**
 * Ce que le membre a choisi, qui n'est pas la meme chose que ce qu'il
 * voit : « systeme » se resout en jour ou en nuit selon le reglage du
 * telephone, et change avec lui.
 */
export type AmbiancePreference = 'system' | Ambiance;

/** Preference retenue d'un lancement a l'autre, avant meme la session. */
const CLE = 'sirocco:ambiance-preference';

/**
 * Le bleu de nuit de l'illustration.
 * Fond, surfaces et bordures viennent de cette meme teinte, a des
 * clartes croissantes : c'est ce qui donne au mode nuit sa cohesion.
 */
const NUIT = { teinte: 220, saturation: 34, clarte: 9 } as const;

/**
 * L'echelle des fonds, en points de clarte au-dessus du plancher.
 *
 * Ce sont les ecarts qui font la profondeur, pas les valeurs absolues :
 * les garder constants permet de relever la nuit d'une ville sans
 * rapprocher ses plans. Le « soft » de l'accent suit la meme echelle,
 * sinon un fond releve l'avalerait.
 */
const ETAGES = { surface: 5, surfaceMuted: 10, primarySoft: 12, border: 17 } as const;

/**
 * Le reglage d'apparence du telephone.
 *
 * `prefers-color-scheme` est renseigne par iOS dans la vue web, exactement
 * comme dans Safari : c'est ce qui permet de suivre le reglage systeme
 * sans passer par un module natif.
 */
export function systemAmbiance(): Ambiance {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
  } catch {
    return 'day';
  }
}

/**
 * Previent quand le telephone bascule, et rend de quoi se desabonner.
 * L'utilisateur peut changer le reglage sans quitter l'application —
 * depuis le centre de controle, ou au coucher du soleil si la bascule
 * automatique d'iOS est active.
 */
export function watchSystemAmbiance(onChange: (ambiance: Ambiance) => void): () => void {
  const relire = () => onChange(systemAmbiance());

  /*
    Deux declencheurs pour une seule question.

    L'evenement de la requete media est le bon outil, et il suffit quand
    l'application est a l'ecran. Mais le cas le plus courant sur un
    telephone, c'est de basculer l'apparence depuis le centre de controle
    ou les reglages — donc pendant que l'application est en arriere-plan,
    ou WebKit ne garantit pas de la reveiller pour si peu. Le retour au
    premier plan repose donc la question, et la reponse est la meme.
  */
  try {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', relire);
    document.addEventListener('visibilitychange', relire);
    window.addEventListener('focus', relire);

    return () => {
      media.removeEventListener('change', relire);
      document.removeEventListener('visibilitychange', relire);
      window.removeEventListener('focus', relire);
    };
  } catch {
    return () => {};
  }
}

/**
 * La preference retenue sur cet appareil.
 *
 * Elle est locale a dessein : « suivre le systeme » ne veut rien dire
 * d'un telephone a l'autre, et c'est bien l'appareil qu'on regarde.
 * Rien de retenu, donc on suit le telephone — c'est ce qu'on attend
 * d'une application qu'on ouvre pour la premiere fois.
 */
export function readAmbiancePreference(): AmbiancePreference {
  try {
    const retenu = localStorage.getItem(CLE);
    return retenu === 'day' || retenu === 'night' ? retenu : 'system';
  } catch {
    // Navigation privee, stockage refuse : on suit le telephone.
    return 'system';
  }
}

export function rememberAmbiancePreference(preference: AmbiancePreference): void {
  try {
    localStorage.setItem(CLE, preference);
  } catch {
    // Sans stockage, la preference ne survit pas au rechargement. Tant pis.
  }
}

/** Ce qu'il faut peindre, une fois la preference confrontee au telephone. */
export function resolveAmbiance(preference: AmbiancePreference): Ambiance {
  return preference === 'system' ? systemAmbiance() : preference;
}

/**
 * La version nocturne d'un theme de spot.
 *
 * Les fonds descendent vers le bleu profond du ciel, le texte remonte
 * vers un blanc chaud — celui des fenetres allumees — et l'accent
 * s'eclaircit sans changer de teinte.
 */
export function nightTheme(theme: ServerTheme): ServerTheme {
  const { h, s } = toHsl(theme.primary);
  const accent = clamp(s, 55, 88);

  // La ville peut refroidir sa nuit ; sinon c'est le bleu par defaut.
  const teinte = theme.nightHue ?? NUIT.teinte;
  const saturation = theme.nightSaturation ?? NUIT.saturation;
  const plancher = theme.nightLightness ?? NUIT.clarte;

  return {
    illustration: theme.illustration,
    primary: hsl(h, accent, 62),
    primaryDark: hsl(h, accent, 52),
    // Sur fond sombre, un "soft" est une teinte foncee, pas un pastel.
    primarySoft: hsl(h, 42, plancher + ETAGES.primarySoft),
    background: hsl(teinte, saturation, plancher),
    surface: hsl(teinte, saturation - 4, plancher + ETAGES.surface),
    surfaceMuted: hsl(teinte, saturation - 8, plancher + ETAGES.surfaceMuted),
    text: hsl(36, 32, 93),
    textMuted: hsl(teinte, 16, 68),
    border: hsl(teinte, 22, plancher + ETAGES.border),
  };
}

/** Le theme reellement applique, selon l'heure choisie par le membre. */
export function themeFor(theme: ServerTheme, ambiance: Ambiance): ServerTheme {
  return ambiance === 'night' ? nightTheme(theme) : theme;
}

/* --- Couleurs ------------------------------------------------------- */

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hsl(h: number, s: number, l: number): string {
  return `hsl(${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%)`;
}

/** "#B8451B" -> teinte, saturation et clarte, en degres et pourcents. */
function toHsl(hex: string): { h: number; s: number; l: number } {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value;
  const int = Number.parseInt(full, 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return { h: 0, s: 0, l: l * 100 };

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / delta) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  return { h: (h * 60 + 360) % 360, s: s * 100, l: l * 100 };
}
